import { pageSourceColumns } from '../db/workspace-tables'
import { crdt } from '@w/crdt'
import type { Block } from '@w/content-blocks'
import type { FlashcardsFeRouterCtx } from '../router/flashcards-fe.router-ctx'
import { commitWorkspacePageEdit } from '../db/page-write'
import { pageDocumentInput, readPageDocument } from '../db/page-document'
import { mintQuestionBlocks, hasQid } from './entry-blocks'
import { editEntryQuestions } from './entry-question-edit'
import { ulid } from '@w/utils'
import { nativeTongueFor } from '../translation/native-language'

/** The stored questions are the learning selection; the entry retains their identities. */
export async function setSenseLearning(ctx: FlashcardsFeRouterCtx, spaceId: string, pageId: string, senseId: string, selected: boolean) {
  await ctx.writes.flush(spaceId)
  const db = ctx.db(spaceId)
  const row = await db.query`SELECT ${pageSourceColumns(db.pages)} FROM ${db.pages}
    WHERE ${db.pages.id.eq(pageId)} AND ${db.pages.deletedAt.isNull()}`.executeOneOrThrow(db.pages.__selectSchema)
  const doc = crdt.load(row.data, { user: ctx.auth.getUser().id })
  const content = readPageDocument(doc.toJSON(), row).content
  if (content.kind !== 'lexiconEntry') throw new Error('Expected entry article')
  const current = content.entry
  const next = structuredClone(current)
  const sense = next.senses[senseId]
  if (!sense) throw new Error('Sense unavailable')
  const l1 = nativeTongueFor(ctx.auth.getUser().settings.lexicon?.nativeLanguage, ctx.defaultNativeLanguage)
  const since = doc.version()
  doc.edit(tx => {
    if (selected && !sense.senseRecallQuestionId) {
      sense.senseRecallQuestionId = ulid()
      sense.formRecallQuestionId = ulid()
      editEntryQuestions(tx, current, next, l1)
    }
    const qids = new Set([sense.senseRecallQuestionId, sense.formRecallQuestionId].filter((qid): qid is string => Boolean(qid)))
    const children = tx.read('content.children') as unknown as Block[]
    if (selected) {
      for (const qid of qids) {
        if (children.some(block => hasQid(block, new Set([qid])))) continue
        const kind = qid === sense.senseRecallQuestionId ? 'senseRecall' : 'formRecall'
        const blocks = mintQuestionBlocks(next, qid, kind, l1)
        tx.insert('content.children', (tx.read('content.children') as unknown[]).length,
          pageDocumentInput(blocks) as Parameters<typeof tx.insert>[2])
      }
    } else {
      for (let index = children.length - 1; index >= 0; index--) {
        if (hasQid(children[index], qids)) tx.remove('content.children', index, 1)
      }
    }
  })
  if (doc.version() === since) return
  await commitWorkspacePageEdit(ctx, spaceId, { fields: { content: { ...content, entry: next } }, pageId: row.id, userId: ctx.auth.getUser().id, now: new Date(), frame: doc.encodeUpdate(since).bytes })
}
